import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPatch, apiPost } from '../utils/api';

type WebsiteSettings = {
  marketplace_visible: boolean;
  rwanda_phone: string;
  company_status: string;
};

type PageContent = {
  page_key: string;
  title: string;
  summary: string;
  content: Record<string, any>;
  visible: boolean;
  updated_at?: string;
};

type Inquiry = {
  id: number;
  name: string;
  organization?: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  source: string;
  status: 'new' | 'reviewed' | 'replied' | 'archived';
  created_at: string;
};

type Subscriber = {
  id: number;
  email: string;
  source: string;
  status: 'active' | 'archived' | 'unsubscribed';
  created_at: string;
};

type BlogPost = {
  id?: number;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  author: string;
  category: string;
  tags: string[];
  image_url: string;
  published: boolean;
  featured: boolean;
  published_at?: string;
  updated_at?: string;
};

type Summary = {
  inquiries: { total: number; new_count: number; week_count: number };
  subscribers: { total: number; active_count: number; week_count: number };
  pages: { total: number; visible_count: number };
};

const defaults: WebsiteSettings = {
  marketplace_visible: false,
  rwanda_phone: '+250 788 635 555',
  company_status: 'Licensed agricultural technology company',
};

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'pages', label: 'Pages' },
  { id: 'stories', label: 'Stories' },
  { id: 'inbox', label: 'Contact Inbox' },
  { id: 'subscribers', label: 'Subscriptions' },
  { id: 'settings', label: 'Settings' },
] as const;

type TabId = typeof tabs[number]['id'];

const statusStyles: Record<string, string> = {
  new: 'bg-amber-50 text-amber-700 border-amber-200',
  reviewed: 'bg-blue-50 text-blue-700 border-blue-200',
  replied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-50 text-slate-600 border-slate-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unsubscribed: 'bg-red-50 text-red-700 border-red-200',
};

const formatDate = (value?: string) => {
  if (!value) return 'Not updated';
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const WebsiteControlPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [settings, setSettings] = useState<WebsiteSettings>(defaults);
  const [pages, setPages] = useState<PageContent[]>([]);
  const [selectedPageKey, setSelectedPageKey] = useState('home');
  const [editingPage, setEditingPage] = useState<PageContent | null>(null);
  const [contentDraft, setContentDraft] = useState('{}');
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [editingPost, setEditingPost] = useState<BlogPost>({
    slug: '',
    title: '',
    excerpt: '',
    body: '',
    author: 'eSanza Team',
    category: 'Field Story',
    tags: [],
    image_url: '',
    published: false,
    featured: false,
  });
  const [summary, setSummary] = useState<Summary | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'saving' | 'saved' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const selectedPage = useMemo(
    () => pages.find((page) => page.page_key === selectedPageKey) || pages[0],
    [pages, selectedPageKey]
  );

  const loadAll = async () => {
    setStatus('loading');
    setMessage('');
    try {
      const [settingsData, summaryData, pagesData, inquiriesData, subscribersData, postsData] = await Promise.all([
        apiGet<{ success: boolean; settings: WebsiteSettings }>('/api/website-control'),
        apiGet<{ success: boolean; summary: Summary }>('/api/website-control/summary'),
        apiGet<{ success: boolean; pages: PageContent[] }>('/api/website-control/pages'),
        apiGet<{ success: boolean; inquiries: Inquiry[] }>('/api/website-control/inquiries'),
        apiGet<{ success: boolean; subscribers: Subscriber[] }>('/api/website-control/subscribers'),
        apiGet<{ success: boolean; posts: BlogPost[] }>('/api/website-control/blog-posts'),
      ]);
      setSettings({ ...defaults, ...settingsData.settings });
      setSummary(summaryData.summary);
      setPages(pagesData.pages || []);
      setSelectedPageKey(pagesData.pages?.[0]?.page_key || 'home');
      setInquiries(inquiriesData.inquiries || []);
      setSubscribers(subscribersData.subscribers || []);
      setPosts(postsData.posts || []);
      setStatus('idle');
    } catch (error: any) {
      setMessage(error.message || 'Could not load website controls.');
      setStatus('error');
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      setEditingPage({
        ...selectedPage,
        content: selectedPage.content || {},
      });
      setContentDraft(JSON.stringify(selectedPage.content || {}, null, 2));
    }
  }, [selectedPage]);

  const saveSettings = async () => {
    setStatus('saving');
    setMessage('');
    try {
      const data = await apiPatch<{ success: boolean; settings: WebsiteSettings }>('/api/website-control', settings);
      setSettings({ ...defaults, ...data.settings });
      setStatus('saved');
      setMessage('Website controls saved.');
      loadAll();
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Could not save website controls.');
    }
  };

  const savePage = async () => {
    if (!editingPage) return;
    setStatus('saving');
    setMessage('');
    try {
      const parsedContent = JSON.parse(contentDraft || '{}');
      const data = await apiPatch<{ success: boolean; page: PageContent }>(
        `/api/website-control/pages/${editingPage.page_key}`,
        { ...editingPage, content: parsedContent }
      );
      setPages((prev) => prev.map((page) => page.page_key === data.page.page_key ? data.page : page));
      setEditingPage(data.page);
      setContentDraft(JSON.stringify(data.page.content || {}, null, 2));
      setStatus('saved');
      setMessage(`${data.page.title} content saved.`);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Could not save page content. Check JSON formatting.');
    }
  };

  const getPreviewPath = (pageKey: string) => {
    const paths: Record<string, string> = {
      home: '/',
      about: '/about',
      platform: '/platform',
      traceability: '/traceability',
      impact: '/impact',
      contact: '/contact',
      blog: '/blog',
      marketplace: '/marketplace',
      support: '/support',
      aaywa: '/aaywa',
    };
    return paths[pageKey] || '/';
  };

  const previewPage = () => {
    if (!editingPage) return;
    window.open(getPreviewPath(editingPage.page_key), '_blank', 'noopener,noreferrer');
  };

  const updateInquiryStatus = async (id: number, nextStatus: Inquiry['status']) => {
    const data = await apiPatch<{ success: boolean; inquiry: Inquiry }>(`/api/website-control/inquiries/${id}`, { status: nextStatus });
    setInquiries((prev) => prev.map((item) => item.id === id ? data.inquiry : item));
    loadAll();
  };

  const updateSubscriberStatus = async (id: number, nextStatus: Subscriber['status']) => {
    const data = await apiPatch<{ success: boolean; subscriber: Subscriber }>(`/api/website-control/subscribers/${id}`, { status: nextStatus });
    setSubscribers((prev) => prev.map((item) => item.id === id ? data.subscriber : item));
    loadAll();
  };

  const resetPostForm = () => {
    setEditingPost({
      slug: '',
      title: '',
      excerpt: '',
      body: '',
      author: 'eSanza Team',
      category: 'Field Story',
      tags: [],
      image_url: '',
      published: false,
      featured: false,
    });
  };

  const savePost = async () => {
    setStatus('saving');
    setMessage('');
    try {
      const payload = {
        ...editingPost,
        tags: Array.isArray(editingPost.tags)
          ? editingPost.tags
          : String(editingPost.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean),
      };
      const data = editingPost.id
        ? await apiPatch<{ success: boolean; post: BlogPost }>(`/api/website-control/blog-posts/${editingPost.id}`, payload)
        : await apiPost<{ success: boolean; post: BlogPost }>('/api/website-control/blog-posts', payload);

      setPosts((prev) => {
        const exists = prev.some((post) => post.id === data.post.id);
        return exists ? prev.map((post) => post.id === data.post.id ? data.post : post) : [data.post, ...prev];
      });
      setEditingPost(data.post);
      setStatus('saved');
      setMessage(`Story "${data.post.title}" saved.`);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Could not save story.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-700">Public website</p>
          <h1 className="mt-2 text-3xl font-black text-slate-950">Public Website</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Manage public website pages, blog stories, contact inquiries, newsletter subscriptions, and visibility settings.
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={status === 'loading'}
          className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-slate-700 hover:border-emerald-700 hover:text-emerald-700 disabled:opacity-60"
        >
          Refresh
        </button>
      </div>

      {message && (
        <div className={`rounded-lg border p-4 text-sm font-semibold ${status === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {message}
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto rounded-lg border border-slate-200 bg-white p-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-bold transition ${activeTab === tab.id ? 'bg-emerald-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            {[
              ['New inquiries', summary?.inquiries?.new_count ?? 0],
              ['Total inquiries', summary?.inquiries?.total ?? 0],
              ['Subscribers', summary?.subscribers?.active_count ?? 0],
              ['Visible pages', `${summary?.pages?.visible_count ?? 0}/${summary?.pages?.total ?? 0}`],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</p>
                <p className="mt-3 text-3xl font-black text-slate-950">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-black text-slate-950">Current configuration</h2>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Public marketplace</span>
                  <span className="font-bold text-slate-900">{settings.marketplace_visible ? 'Visible' : 'Hidden'}</span>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Contact phone</span>
                  <span className="font-bold text-slate-900">{settings.rwanda_phone}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Company status</span>
                  <span className="font-bold text-slate-900">{settings.company_status}</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-black text-slate-950">Admin notifications</h2>
              <div className="mt-5 space-y-3">
                {(summary?.inquiries?.new_count ?? 0) > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    {summary?.inquiries?.new_count} contact {summary?.inquiries?.new_count === 1 ? 'inquiry' : 'inquiries'} need review.
                  </div>
                )}
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                  Website control is connected to real contact and subscription data.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pages' && (
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            {pages.map((page) => (
              <button
                key={page.page_key}
                onClick={() => setSelectedPageKey(page.page_key)}
                className={`mb-2 w-full rounded-lg p-4 text-left transition ${selectedPageKey === page.page_key ? 'bg-emerald-700 text-white' : 'hover:bg-slate-50'}`}
              >
                <span className="block text-sm font-black">{page.title}</span>
                <span className={`mt-1 block text-xs ${selectedPageKey === page.page_key ? 'text-white/70' : 'text-slate-500'}`}>{page.visible ? 'Visible' : 'Hidden'}</span>
              </button>
            ))}
          </div>

          {editingPage && (
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-black text-slate-950">{editingPage.title}</h2>
                  <p className="mt-1 text-sm text-slate-500">Last update: {formatDate(editingPage.updated_at)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={previewPage} className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-slate-700 hover:border-emerald-700 hover:text-emerald-700">
                    Preview page
                  </button>
                  <button onClick={savePage} className="rounded-lg bg-emerald-700 px-5 py-3 text-sm font-extrabold text-white hover:bg-emerald-800">
                    Save page
                  </button>
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Page title</span>
                  <input
                    value={editingPage.title}
                    onChange={(event) => setEditingPage((prev) => prev ? { ...prev, title: event.target.value } : prev)}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                  />
                </label>
                <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <span>
                    <span className="block text-sm font-bold text-slate-700">Visible on public website</span>
                    <span className="mt-1 block text-xs text-slate-500">Use this as an approval flag for future dynamic publishing.</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={editingPage.visible}
                    onChange={(event) => setEditingPage((prev) => prev ? { ...prev, visible: event.target.checked } : prev)}
                    className="h-5 w-5"
                  />
                </label>
              </div>

              <label className="mt-4 block">
                <span className="text-sm font-bold text-slate-700">Summary</span>
                <textarea
                  value={editingPage.summary || ''}
                  onChange={(event) => setEditingPage((prev) => prev ? { ...prev, summary: event.target.value } : prev)}
                  rows={3}
                  className="mt-2 w-full resize-none rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>

              <label className="mt-4 block">
                <span className="text-sm font-bold text-slate-700">Structured content JSON</span>
                <textarea
                  value={contentDraft}
                  onChange={(event) => setContentDraft(event.target.value)}
                  rows={10}
                  className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-slate-950 px-4 py-3 font-mono text-xs text-emerald-100 outline-none focus:border-emerald-700"
                />
              </label>
            </div>
          )}
        </div>
      )}

      {activeTab === 'stories' && (
        <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-slate-950">{editingPost.id ? 'Edit story' : 'Create story'}</h2>
                <p className="mt-1 text-sm text-slate-500">Publish public blog/story content from the dashboard.</p>
              </div>
              {editingPost.id && (
                <button onClick={resetPostForm} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:border-emerald-700 hover:text-emerald-700">
                  New
                </button>
              )}
            </div>

            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Title</span>
                <input
                  value={editingPost.title}
                  onChange={(event) => setEditingPost((prev) => ({ ...prev, title: event.target.value }))}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Slug</span>
                <input
                  value={editingPost.slug}
                  onChange={(event) => setEditingPost((prev) => ({ ...prev, slug: event.target.value }))}
                  placeholder="auto-created from title if empty"
                  className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Image URL</span>
                <input
                  value={editingPost.image_url || ''}
                  onChange={(event) => setEditingPost((prev) => ({ ...prev, image_url: event.target.value }))}
                  placeholder="/images/blogs/story.jpeg"
                  className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Author</span>
                  <input
                    value={editingPost.author}
                    onChange={(event) => setEditingPost((prev) => ({ ...prev, author: event.target.value }))}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Category</span>
                  <input
                    value={editingPost.category}
                    onChange={(event) => setEditingPost((prev) => ({ ...prev, category: event.target.value }))}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Tags</span>
                <input
                  value={(editingPost.tags || []).join(', ')}
                  onChange={(event) => setEditingPost((prev) => ({ ...prev, tags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean) }))}
                  placeholder="Traceability, Training, Rwanda"
                  className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Excerpt</span>
                <textarea
                  value={editingPost.excerpt || ''}
                  onChange={(event) => setEditingPost((prev) => ({ ...prev, excerpt: event.target.value }))}
                  rows={3}
                  className="mt-2 w-full resize-none rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Body</span>
                <textarea
                  value={editingPost.body}
                  onChange={(event) => setEditingPost((prev) => ({ ...prev, body: event.target.value }))}
                  rows={8}
                  className="mt-2 w-full resize-y rounded-lg border border-slate-200 px-4 py-3 text-sm leading-6 outline-none focus:border-emerald-700"
                />
              </label>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4 text-sm font-bold text-slate-700">
                  Published
                  <input type="checkbox" checked={editingPost.published} onChange={(event) => setEditingPost((prev) => ({ ...prev, published: event.target.checked }))} className="h-5 w-5" />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4 text-sm font-bold text-slate-700">
                  Featured
                  <input type="checkbox" checked={editingPost.featured} onChange={(event) => setEditingPost((prev) => ({ ...prev, featured: event.target.checked }))} className="h-5 w-5" />
                </label>
              </div>
              <button onClick={savePost} disabled={status === 'saving'} className="w-full rounded-lg bg-emerald-700 px-5 py-3 text-sm font-extrabold text-white hover:bg-emerald-800 disabled:opacity-60">
                {status === 'saving' ? 'Saving...' : 'Save story'}
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-5">
              <h2 className="text-lg font-black text-slate-950">Stories</h2>
              <p className="mt-1 text-sm text-slate-500">Published stories appear on the public blog.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {posts.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No stories yet.</div>
              ) : posts.map((post) => (
                <button
                  key={post.id || post.slug}
                  onClick={() => setEditingPost({ ...post, image_url: post.image_url || '', tags: post.tags || [] })}
                  className="block w-full p-5 text-left transition hover:bg-slate-50"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-base font-black text-slate-950">{post.title}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{post.excerpt || post.body}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${post.published ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                        {post.featured && <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">Featured</span>}
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-600">{post.category}</span>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-slate-400">{formatDate(post.published_at || post.updated_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'inbox' && (
        <div className="space-y-4">
          {inquiries.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">No contact inquiries yet.</div>
          ) : inquiries.map((item) => (
            <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-black text-slate-950">{item.subject}</h3>
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusStyles[item.status] || statusStyles.archived}`}>{item.status}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">{item.name} {item.organization ? `- ${item.organization}` : ''}</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-600">
                    <a href={`mailto:${item.email}`} className="font-semibold text-emerald-700">{item.email}</a>
                    {item.phone && <a href={`tel:${item.phone}`} className="font-semibold text-emerald-700">{item.phone}</a>}
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </div>
                <select
                  value={item.status}
                  onChange={(event) => updateInquiryStatus(item.id, event.target.value as Inquiry['status'])}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold outline-none focus:border-emerald-700"
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="replied">Replied</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <p className="mt-5 whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-sm leading-6 text-slate-700">{item.message}</p>
            </article>
          ))}
        </div>
      )}

      {activeTab === 'subscribers' && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-5">
            <h2 className="text-lg font-black text-slate-950">Newsletter subscriptions</h2>
            <p className="mt-1 text-sm text-slate-500">People who asked for eSanza updates from the public website.</p>
          </div>
          <div className="divide-y divide-slate-100">
            {subscribers.length === 0 ? (
              <div className="p-10 text-center text-slate-500">No subscribers yet.</div>
            ) : subscribers.map((item) => (
              <div key={item.id} className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <a href={`mailto:${item.email}`} className="font-bold text-slate-950 hover:text-emerald-700">{item.email}</a>
                  <p className="mt-1 text-xs text-slate-500">Joined {formatDate(item.created_at)} from {item.source}</p>
                </div>
                <select
                  value={item.status}
                  onChange={(event) => updateSubscriberStatus(item.id, event.target.value as Subscriber['status'])}
                  className={`rounded-lg border px-4 py-2 text-sm font-bold outline-none ${statusStyles[item.status] || statusStyles.active}`}
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-black text-slate-950">Visibility</h2>
              <div className="mt-5 space-y-4">
                <label className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <span>
                    <span className="block text-sm font-bold text-slate-900">Public marketplace</span>
                    <span className="mt-1 block text-sm text-slate-500">Show or hide the public marketplace catalogue on esanza.com.</span>
                  </span>
                  <input type="checkbox" checked={settings.marketplace_visible} onChange={(event) => setSettings((prev) => ({ ...prev, marketplace_visible: event.target.checked }))} className="mt-1 h-5 w-5" />
                </label>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-black text-slate-950">Contact & Status</h2>
              <div className="mt-5 space-y-4">
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Contact phone</span>
                  <input value={settings.rwanda_phone} onChange={(event) => setSettings((prev) => ({ ...prev, rwanda_phone: event.target.value }))} className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700" />
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Company status</span>
                  <input value={settings.company_status} onChange={(event) => setSettings((prev) => ({ ...prev, company_status: event.target.value }))} className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700" />
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveSettings} disabled={status === 'saving'} className="rounded-lg bg-emerald-700 px-5 py-3 text-sm font-extrabold text-white hover:bg-emerald-800 disabled:opacity-60">
              {status === 'saving' ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteControlPage;
